/**
 * LeadForm — Landing → CRM lead capture («Қара алтын» edition).
 *
 * Логика НЕ менялась (Edge Function landingLeadSubmit + honeypot +
 * IP rate-limit) — только editorial-обёртка: гигантский serif-заголовок
 * слева, форма справа.
 */
import { useEffect, useState } from "react";
import { Check, AlertCircle, ArrowRight, SlidersHorizontal } from "lucide-react";
import { useLang } from "../i18n";
import { useReveal } from "../lib/motion";
import { clearLeadNote, getLeadNote, subscribeLeadNote } from "../lib/leadNote";

type Phase = "form" | "submitting" | "success" | "error";

const SUPABASE_URL =
  (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL) ||
  "https://yaqsyjtgehpvrogthmwo.supabase.co";
const SUPABASE_ANON_KEY =
  (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_ANON_KEY) ||
  "";

export function LeadForm() {
  const { t } = useLang();
  const ref = useReveal<HTMLElement>();
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [phase, setPhase] = useState<Phase>("form");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  // Конфигурация из конструктора тарифа (07): уходит в note заявки.
  const [builderNote, setBuilderNote] = useState<string | null>(() => getLeadNote());
  useEffect(() => subscribeLeadNote(setBuilderNote), []);

  const phoneValid = phone.replace(/\D/g, "").length >= 8;
  const canSubmit = phoneValid && phase === "form";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setPhase("submitting");
    setErrMsg(null);

    try {
      const sourceUrl = typeof window !== "undefined" ? window.location.href : "";
      const res = await fetch(`${SUPABASE_URL}/functions/v1/landingLeadSubmit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          // Форма упрощена до одного поля — телефон (клиенту не нужно много вводить).
          // Бэкенд требует name>=2, поэтому имя = телефон (в CRM лид виден по номеру).
          name: phone.trim(),
          phone: phone.trim(),
          source_url: sourceUrl,
          website: website,
          // Конфигурация из конструктора (если собирали) — менеджеру в CRM/Telegram.
          ...(builderNote ? { note: builderNote } : {}),
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = (body as { error?: string }).error ?? `http_${res.status}`;
        setErrMsg(translateError(code, t.lead));
        setPhase("error");
        return;
      }

      setPhase("success");
      setPhone("");
      clearLeadNote();
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : String(err));
      setPhase("error");
    }
  }

  const lead = t.lead;

  return (
    <section
      id="lead"
      ref={ref}
      className="relative overflow-hidden border-t"
      style={{ borderColor: "var(--line)" }}
    >
      {/* Золотое свечение снизу */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[60%]"
        aria-hidden
        style={{
          background:
            "radial-gradient(70% 100% at 50% 100%, rgba(212,166,86,0.10), transparent 70%)",
        }}
      />

      <div
        className="relative mx-auto grid w-full max-w-7xl gap-8 px-5 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:gap-14"
        style={{ paddingTop: "var(--space-section)", paddingBottom: "var(--space-section)" }}
      >
        <div>
          <div className="section-rule mb-8">
            <span className="t-eyebrow reveal" data-reveal="">
              <b>09</b>&nbsp;/&nbsp;{lead.label}
            </span>
          </div>
          <h2 className="reveal mb-6" data-reveal="">
            <span
              className="t-serif-i block"
              style={{ fontSize: "var(--text-display)", color: "var(--fg)", lineHeight: 1.05 }}
            >
              {lead.title}
            </span>
          </h2>
          <p
            className="reveal max-w-md text-base leading-relaxed sm:text-lg"
            data-reveal=""
            style={{ color: "var(--fg-soft)", "--reveal-delay": "120ms" } as React.CSSProperties}
          >
            {lead.subtitle}
          </p>
        </div>

        {phase === "success" ? (
          <SuccessState title={lead.successTitle} body={lead.successBody} />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="reveal rounded-3xl p-7 sm:p-9"
            data-reveal=""
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--line-strong)",
              "--reveal-delay": "180ms",
            } as React.CSSProperties}
          >
            {/* Honeypot — invisible to humans, bots fill it */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
              aria-hidden
            />

            <div className="space-y-5">
              {/* Только телефон — минимум ввода для клиента. Остальное менеджер уточнит при звонке. */}
              <Field label={lead.phoneLabel} required>
                <input
                  type="tel"
                  className="f-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+7 701 234 56 78"
                  required
                  inputMode="tel"
                />
              </Field>
            </div>

            {builderNote && (
              <div
                className="mt-5 flex items-start gap-2.5 rounded-2xl p-3.5 text-sm"
                style={{
                  background: "rgba(212,166,86,0.08)",
                  border: "1px solid var(--gold-line)",
                  color: "var(--gold-bright)",
                }}
              >
                <SlidersHorizontal className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
                <span>
                  {lead.configAttached}
                  <span className="mt-0.5 block text-xs" style={{ color: "var(--fg-soft)" }}>
                    {builderNote.split("\n")[0]}
                  </span>
                </span>
              </div>
            )}

            {phase === "error" && errMsg && (
              <div
                className="mt-5 flex items-start gap-2 rounded-2xl p-3.5 text-sm"
                style={{
                  background: "rgba(239,68,68,0.10)",
                  border: "1px solid rgba(239,68,68,0.35)",
                  color: "#FCA5A5",
                }}
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
                <span>{errMsg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="btn-gold mt-7 w-full"
              style={
                canSubmit
                  ? undefined
                  : { background: "var(--bg-elev)", color: "var(--fg-muted)", cursor: "not-allowed" }
              }
            >
              {phase === "submitting" ? lead.submitting : lead.submit}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>

            <p className="mt-4 text-center text-xs" style={{ color: "var(--fg-muted)" }}>
              {lead.privacy}
            </p>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, required, children }: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span
        className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em]"
        style={{ color: "var(--fg-muted)" }}
      >
        {label}
        {required && <span style={{ color: "var(--gold)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

function SuccessState({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-3xl p-10 text-center"
      style={{ background: "var(--bg-card)", border: "1px solid var(--gold-line)" }}
    >
      <div
        className="mb-5 flex h-16 w-16 items-center justify-center rounded-full"
        style={{ background: "rgba(212,166,86,0.14)", color: "var(--gold)" }}
      >
        <Check className="h-8 w-8" aria-hidden />
      </div>
      <h3 className="t-display mb-3 text-2xl" style={{ color: "var(--fg)" }}>
        {title}
      </h3>
      <p style={{ color: "var(--fg-soft)" }}>{body}</p>
    </div>
  );
}

function translateError(code: string, lead: { errors: Record<string, string> }): string {
  return lead.errors[code] ?? lead.errors.unknown ?? code;
}
